import type { Maybe } from '@adornis/base/utilTypes.js';
import { InstitutionOffer } from '@adornis/bh-registration/api/institution-offer.js';
import {
  institutionOfferContext,
  RegistrationController,
  registrationControllerContext,
} from '@adornis/bh-registration/client/bh-registration-controller.js';
import type { BHTranslationDictionary } from '@adornis/buchungsheld-db/translations/translation.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { BuildifyLitElement } from '@adornis/html-based-buildify/client/BuildifyLitElement.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { consume } from '@lit/context';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { takeUntil, type Subject } from 'rxjs';
import type { HTMLBaseInstitutionProducts } from '../../../db/HTMLBaseInstitutionProductFilter.js';
import { renderGroupedProductsByCategory } from './helper/renderGroupedProducts.js';

@customElement('node-institution-products')
export class NodeInstitutionProducts extends BuildifyLitElement<HTMLBaseInstitutionProducts> {
  override content({ content, mode }) {
    if (mode === 'edit') return html` <node-institution-products-edit></node-institution-products-edit> `;
    return html` <node-institution-products-view .content=${content}></node-institution-products-view> `;
  }
}

@customElement('node-institution-products-edit')
export class NodeInstitutionProductsEdit extends ChemistryLitElement {
  override render() {
    return html`
      <tr-flex padding="md" center crossaxis-center ${css({ background: '#333', color: '#fff', borderRadius: '8px' })}>
        <tr-text> Platzhalter für die Produktliste </tr-text>
      </tr-flex>
    `;
  }
}

@customElement('node-institution-products-view')
export class NodeInstitutionProductsView extends ChemistryLitElement {
  @property({ attribute: false }) content?: HTMLBaseInstitutionProducts;

  @consume({ context: registrationControllerContext })
  @state()
  readonly _registrationController!: RegistrationController;

  @consume({ context: institutionOfferContext, subscribe: true })
  @state()
  readonly offer?: Maybe<InstitutionOffer>;

  @state() readonly _translation = new TranslationController<BHTranslationDictionary>(this);

  override connectedCallback(): void {
    super.connectedCallback();

    const controller = this._registrationController;
    if (!controller) throw new Error('no registration controller was consumed');
    const content = this.content;
    if (!content) throw new Error('no content found');

    (this._registrationController.subject as any as Subject<boolean>)
      .asObservable()
      .pipe(takeUntil(this.disconnected))
      .subscribe(() => {
        this.requestUpdate();
      });
  }

  override render() {
    const controller = this._registrationController;

    if (!controller) return html` kein controller found till now... `;

    const offer = this.offer;
    if (!offer) return html`<x-loading-state></x-loading-state>`;

    const numberOfProducts = offer.products.length;
    const categoryFilterIsActive = (this._registrationController.categorieConstraints$.value.length ?? 0) > 0;
    if (categoryFilterIsActive && numberOfProducts === 0)
      return html`<x-text ${css({ margin: '1rem 0' })}
        ><i>${this._translation.translate('REGISTRATION', 'category_is_empty')}</i></x-text
      >`;
    if (numberOfProducts === 0)
      return html`<x-text ${css({ margin: '1rem 0' })}
        ><i>${this._translation.translate('REGISTRATION', 'no_results')}</i></x-text
      >`;

    return renderGroupedProductsByCategory({
      offer,
      registrationController: controller,
    });
  }
}
